import { SUPPORTED_CHAIN_SLUGS } from "../chains.js";
/** Use for all-chain core contract tests. */
export const CORE_TEST_CHAINS = [
    ...SUPPORTED_CHAIN_SLUGS,
];
/**
 * Chains covered by getBlockHeight integration tests. Bitcoin is omitted because
 * its chain config isn't guaranteed to be loaded in every environment (no RPC URL
 * in the DB means the config is skipped and the handler returns `error: 'unsupported'`).
 */
export const CORE_BLOCK_HEIGHT_TEST_CHAINS = SUPPORTED_CHAIN_SLUGS.filter((slug) => slug !== "bitcoin");
/**
 * Canonical block hashes at `CORE_PINNED_BLOCK_HEIGHTS`. Used to drive the
 * getBlockByHash contract tests — keeping both height and hash pinned means
 * the getBlockByNumber and getBlockByHash fixtures reference the same block.
 */
export const CORE_PINNED_BLOCK_HASHES = {
    ethereum: "0xf16da847a49abb4ac50ef7c83aa694bc2186b63a09e96b8f6525a461353e75f4",
    polygon: "0x6f91612637c51a09d5f0dc020595529ed70e1c47ec8dd77d207de347cdaac848",
    bsc: "0x9d386842d7904a6c3a04924bc6275435fbbd4cb6da9fc6ef82825c5773679a49",
    optimism: "0x2513910da95ed0ae4aef5643c2b4a7e0fc6570e5113a528acb8a951cbc041bd3",
    base: "0x351eeb4b496c8bcaa1f297f9f4b4cb5f5d8776c1c60fa42f7a6ca4fe7e15822d",
    avalanche: "0xa363fb9dc118b36ab9e7524b9013c317a7beb9549969a5a2b81e70d3f2cdfeac",
    arbitrum: "0xd57cf8938c42bc72cf8fac9bc7be231321803e672048bd175706bdbfa57668ae",
    gnosis: "0x759994357400b6c9446eba8069503c6d153f468d0f456713fea1c7bbc1f31324",
    berachain: "0xbe1e7e3d4bf731543cfcb121e8577228e04da945b4defcd52a96c7e152a48560",
    moonbeam: "0x1a292df25bdab08077f545e66a3ba5c0daa15298c511db61401eea6151e9194c",
    monad: "0xf1f9dd57ae21d63b8cf49f501de0703a5d1f005d7e63116ef495c30a9ae505ea",
    linea: "0x39488e5ef57217d63a4e237534ff2f3ceead5a4827c66a81ed4719135f6a2cda",
    hyperliquid: "0xa016fc17bb11c639d20802f2b1c9f3fc177c6d5c5ebc8cc787d89b3ad9524431",
    scroll: "0xed950ccda2fb84c08fcd5c4bdfd0cce6476c407e347871099efe9a1ecc30f7bc",
    plasma: "0x892346fc5f148f9752cf4d1797436b441075b04fff50ff4b7e71a8ec48ec132e",
    xlayer: "0x7e372d0f7293ce490ff5534c42254b505e42f296b80c2052f1133c8c37201b49",
    starknet: "0x898fe7f61f5d662199d223de496988f221d150ed054f2fe5e681b2988b9e2c",
};
/**
 * First transaction hash from each chain's pinned block — used to drive the
 * getReceipt contract tests so both endpoints reference the same on-chain event.
 */
export const CORE_PINNED_TRANSACTION_HASHES = {
    ethereum: "0x86c8dae1c2122533b951c2fa2c70ca725239e458c38117476eadf9ebd1726fc5",
    polygon: "0x4cf1476ecbd6170621b1d4390e0dd107cf2fecef556ce562fec7c1bb74dbd538",
    bsc: "0x7b7c5f782b7f6cfd22d7cdb21625a80754a1a2d0a923e25e1f7ffd6d17ee00bf",
    optimism: "0x5fa273f4762ca60402382c8428a4a5f5522ace95ff16b4cd8b443afa4a49691a",
    base: "0x67fa6f3aa36369a33b61edc9eeb28426c74618cf383fa1346ccf787abd9eb8fe",
    avalanche: "0x449847e4a2b1e58c75ababbe2025187d36cb770ff17b25101f58282f42312af7",
    arbitrum: "0x490f1313e2cfff855ef98eb0044ca201dca84eebb8d3876a3674e863cb9eed4c",
    gnosis: "0xb2bbc481846dd6441b04a55c6e1f9e9fe922c6b55bf84aa3fa578f99c05d2706",
    berachain: "0xf5ed454c29219967efe45071cd785511370e9a762f7403fb1fc31b6762f86494",
    moonbeam: "0xc1d03ddc2810f719f4811ba56f2c53b540cd2da6829e5decb2bc1ef33b241db5",
    monad: "0x1d5ff743331a0f50cbf65b32bf294dab9c4d82f13f718c19068d1576fd934891",
    linea: "0x0c9507f545502519c6579f2784ecff02ab807c00b51f60af7eb046c3f637f720",
    hyperliquid: "0xe5ae7e738fa1d07fec33d4774da28458293171c416f21f8f18eb2fc1bf0336c7",
    scroll: "0x6ee963064771a3a7dc12901f1869b1998e931654997d8334e9edd8a3546079e8",
    plasma: "0x45b882c6753513b7addcb7beef16c2ac08435843867d2fb32c3d6c78c9075c09",
    xlayer: "0xa1cb731a8f5206af9e37f300620d8b8d3fead6d627f78a11e89c91438ae8443c",
    starknet: "0x4171cc6a9ce838d37808b16c778526bcaa2bb37b7be877d7397878ceeb39405",
};
/** Use pinned heights where live value drift would make checks flaky. */
export const CORE_PINNED_BLOCK_HEIGHTS = {
    ethereum: 24_928_727,
    polygon: 85_829_828,
    bsc: 93_839_326,
    optimism: 150_590_461,
    base: 44_995_176,
    avalanche: 83_516_898,
    arbitrum: 454_833_892,
    gnosis: 45_785_965,
    berachain: 19_882_491,
    moonbeam: 15_315_773,
    monad: 69_580_004,
    linea: 30_319_419,
    hyperliquid: 33_073_548,
    scroll: 33_795_427,
    plasma: 22_524_762,
    xlayer: 60_687_691,
    solana: 414_705_076,
    bitcoin: 900_000,
    starknet: 600_000,
};
/** Minimum heights for dynamic block-height checks where a stronger floor is stable enough. */
export const CORE_BLOCK_HEIGHT_MIN_HEIGHTS = {
    ...CORE_PINNED_BLOCK_HEIGHTS,
};
